import { useMemo, useState } from 'react';

import type { Session, WorkerNode } from '../types';

export type TerminalSessionLastUsed = Record<string, string>;

export const TERMINAL_SESSION_LAST_USED_STORAGE_KEY = 'mission-control.terminal-session-last-used';
const LEGACY_TERMINAL_SESSION_LAST_USED_STORAGE_KEY = 'proj-creator.terminal-session-last-used';

export type ExecutorTerminalSessionGroup = {
  executor: WorkerNode;
  error?: string | null;
  sessions: Session[];
};

type Props = {
  groups: ExecutorTerminalSessionGroup[];
  loading?: boolean;
  lastUsedAt?: TerminalSessionLastUsed;
  onRefresh?: () => void;
  onSelect: (executorId: string, sessionId: string | null) => void;
  selectedExecutorId?: string | null;
  selectedSessionId?: string | null;
};

export function executorTerminalDisplayName(executor: WorkerNode) {
  return executor.name?.trim() || executor.id;
}

export function executorTerminalSessionDisplayName(session: Session) {
  if (session.name?.trim()) return session.name.trim();
  const profile = session.profile === 'privileged' ? 'Privileged' : 'Maintenance';
  const created = session.created_at.slice(0, 16).replace('T', ' ');
  return `${profile} ${created}`;
}

export function executorTerminalSessionStatusLabel(session: Session) {
  if (session.status === 'expired') return 'expired · retention ended';
  if (session.status === 'failed' && session.status_reason === 'executor_pty_missing') {
    return 'lost · executor restarted';
  }
  if (session.status === 'failed' && session.status_reason === 'start_failed') {
    return 'failed · did not start';
  }
  return session.status;
}

export function executorTerminalSessionStatusDescription(session: Session) {
  if (session.status === 'expired') {
    return 'Expired means the terminal retention period ended. The saved record remains available until retention cleanup, but the host terminal cannot be reattached.';
  }
  if (session.status === 'failed' && session.status_reason === 'executor_pty_missing') {
    return 'The executor restarted or removed the host PTY. The saved record remains available until retention cleanup, but the terminal cannot be reattached.';
  }
  if (session.status === 'failed' && session.status_reason === 'start_failed') {
    return 'The executor could not start this terminal. Start a new terminal after correcting the host issue.';
  }
  if (session.status === 'stopped') {
    return 'This terminal was stopped and cannot be reattached. Start a new terminal to continue.';
  }
  if (session.status === 'failed') {
    return 'This terminal failed and cannot be reattached. Start a new terminal to continue.';
  }
  return null;
}

export function isExecutorTerminalSessionAttachable(session: Session) {
  return session.capabilities.includes('stream_terminal') &&
    !['stopped', 'failed', 'expired'].includes(session.status);
}

function parseTerminalSessionLastUsed(raw: string | null): TerminalSessionLastUsed | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return Object.fromEntries(
      Object.entries(parsed).filter(([, value]) =>
        typeof value === 'string' && Number.isFinite(Date.parse(value))
      )
    );
  } catch {
    return null;
  }
}

export function readTerminalSessionLastUsedFromStorage(
  storage: Pick<Storage, 'getItem' | 'setItem'>
): TerminalSessionLastUsed {
  const canonical = parseTerminalSessionLastUsed(storage.getItem(TERMINAL_SESSION_LAST_USED_STORAGE_KEY));
  if (canonical !== null) return canonical;
  const legacy = parseTerminalSessionLastUsed(storage.getItem(LEGACY_TERMINAL_SESSION_LAST_USED_STORAGE_KEY));
  if (legacy !== null) {
    try {
      storage.setItem(TERMINAL_SESSION_LAST_USED_STORAGE_KEY, JSON.stringify(legacy));
    } catch {
      // Retain the old session order when storage is read-only.
    }
    return legacy;
  }
  return {};
}

export function readTerminalSessionLastUsed(): TerminalSessionLastUsed {
  try {
    return readTerminalSessionLastUsedFromStorage(window.localStorage);
  } catch {
    return {};
  }
}

export function compareExecutorTerminalSessions(
  left: Session,
  right: Session,
  lastUsedAt: TerminalSessionLastUsed
) {
  const leftLastUsed = Date.parse(lastUsedAt[left.id] ?? left.last_seen_at ?? '');
  const rightLastUsed = Date.parse(lastUsedAt[right.id] ?? right.last_seen_at ?? '');
  const leftHasLastUsed = Number.isFinite(leftLastUsed);
  const rightHasLastUsed = Number.isFinite(rightLastUsed);
  if (leftHasLastUsed !== rightHasLastUsed) return leftHasLastUsed ? -1 : 1;
  if (leftHasLastUsed && leftLastUsed !== rightLastUsed) return rightLastUsed - leftLastUsed;

  const createdDifference = Date.parse(right.created_at) - Date.parse(left.created_at);
  return createdDifference || executorTerminalSessionDisplayName(left).localeCompare(
    executorTerminalSessionDisplayName(right)
  ) || left.id.localeCompare(right.id);
}

export function formatTerminalSessionLastUsed(value: string | null | undefined) {
  if (!value || !Number.isFinite(Date.parse(value))) return 'Last used: never';
  const formatted = new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
  return `Last used: ${formatted}`;
}

function searchableText(group: ExecutorTerminalSessionGroup, session?: Session) {
  return [
    executorTerminalDisplayName(group.executor),
    group.executor.id,
    group.executor.status,
    ...(session
      ? [executorTerminalSessionDisplayName(session), session.status, session.shared ? 'shared' : 'private']
      : [])
  ]
    .join(' ')
    .toLocaleLowerCase();
}

export default function ExecutorTerminalSessionDirectory({
  groups,
  loading = false,
  onRefresh,
  onSelect,
  lastUsedAt = {},
  selectedExecutorId,
  selectedSessionId
}: Props) {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleGroups = useMemo(
    () => groups.flatMap((group) => {
      if (!normalizedQuery || searchableText(group).includes(normalizedQuery)) return [group];
      const sessions = group.sessions.filter((session) =>
        searchableText(group, session).includes(normalizedQuery)
      );
      return sessions.length ? [{ ...group, sessions }] : [];
    }),
    [groups, normalizedQuery]
  );
  const sessionCount = groups.reduce((count, group) => count + group.sessions.length, 0);

  return (
    <section className="terminal-directory" aria-label="Terminal sessions">
      <div className="terminal-directory__header">
        <div>
          <strong>Sessions</strong>
          <span>{sessionCount} across {groups.length} executor{groups.length === 1 ? '' : 's'}</span>
        </div>
        {onRefresh && (
          <button
            type="button"
            className="ghost terminal-directory__refresh"
            aria-label={loading ? 'Refreshing terminal workspace' : 'Refresh terminal workspace'}
            title="Refresh terminals"
            disabled={loading}
            onClick={onRefresh}
          >
            ↻
          </button>
        )}
      </div>
      <input
        className="terminal-directory__search"
        type="search"
        aria-label="Search terminals"
        value={query}
        placeholder="Find executor or terminal"
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="terminal-directory__groups">
        {visibleGroups.map((group) => {
          const executorId = group.executor.id;
          const executorSelected = executorId === selectedExecutorId;
          return (
            <section className="terminal-directory__group" key={executorId}>
              <button
                type="button"
                className={`terminal-directory__executor${executorSelected && !selectedSessionId ? ' active' : ''}`}
                aria-label={`Open ${executorTerminalDisplayName(group.executor)} terminal workspace`}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => onSelect(executorId, null)}
              >
                <span>
                  <strong>{executorTerminalDisplayName(group.executor)}</strong>
                  <small>{group.executor.id}</small>
                </span>
                <span className={`terminal-directory__status terminal-directory__status--${group.executor.status.toLocaleLowerCase()}`}>
                  {group.executor.status}
                </span>
              </button>
              {group.error ? (
                <p className="terminal-directory__empty">Sessions unavailable</p>
              ) : group.sessions.length ? (
                <div className="terminal-directory__sessions">
                  {group.sessions.map((session) => (
                    <button
                      type="button"
                      key={session.id}
                      className={
                        executorSelected && session.id === selectedSessionId ? 'active' : ''
                      }
                      aria-current={
                        executorSelected && session.id === selectedSessionId ? 'true' : undefined
                      }
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={() => onSelect(executorId, session.id)}
                    >
                      <span>
                        <strong>{executorTerminalSessionDisplayName(session)}</strong>
                        <small>
                          {session.shared ? 'Shared' : 'Private'} · {formatTerminalSessionLastUsed(
                            lastUsedAt[session.id] ?? session.last_seen_at
                          )}
                          {!['attached', 'detached'].includes(session.status) && ` · ${executorTerminalSessionStatusLabel(session)}`}
                        </small>
                      </span>
                      <span aria-hidden="true">›</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="terminal-directory__empty">No saved sessions</p>
              )}
            </section>
          );
        })}
        {!loading && visibleGroups.length === 0 && (
          <p className="terminal-directory__empty terminal-directory__empty--search">
            No matching terminals.
          </p>
        )}
      </div>
    </section>
  );
}
